import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Camera, FileArchive, RotateCcw, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import {
  deleteMdcpPointPhoto,
  downloadMdcpPhotosZip,
  getMdcpOrderPoints,
  rejectMdcpPhotos,
  restoreMdcpPhotos,
} from "../api";
import { apiErr } from "../utils";
import { buildMdcpLightbox, MdcpPhotoLightbox } from "./MdcpPhotoLightbox";
import { MdcpPhotoRejectModal } from "./MdcpPhotoRejectModal";
import { LinePlacementLegend } from "./LinePlacementLegend";
import {
  MdcpPointPhotoGrid,
  MdcpPointsFootnote,
  MdcpPointsList,
  MdcpSelectedPointHeader,
} from "./MdcpPointsParts";
import { drawMdcpContext, mdcpPointIcon } from "./mdcpMap";

/** Sekcja „Punkty zdjęciowe MDCP" na stronie zamówienia (nasza strona) — self-hiding:
 *  renderuje się tylko dla zamówień MDCP. Podgląd view-only mapy projektu (trasa + działki
 *  + punkty ze strzałkami azymutu) + dokładne współrzędne PUWG punktów. Zdjęcia wgrywa
 *  WYŁĄCZNIE geodeta w swoim zamówieniu (MdcpContractorMap) — tu jest odbiór: klik w punkt
 *  na mapie lub na liście pokazuje jego zdjęcia w panelu obok, gdzie można je odrzucić
 *  (z powodem), przywrócić albo usunąć. Układ i klocki (nagłówek punktu, siatka zdjęć,
 *  lista, stopka) są wspólne z widokiem geodety — MdcpPointsParts. */
export function MdcpPointsPanel({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const key = ["pp", "mdcp-points-order", orderId] as const;
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => getMdcpOrderPoints(orderId),
    retry: false,
    staleTime: 30_000,
  });
  const inval = () => qc.invalidateQueries({ queryKey: key });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Zdjęcia do odrzucenia: null = okno zamknięte, [] = całe zamówienie, lista = wskazane zdjęcia.
  const [rejectTarget, setRejectTarget] = useState<string[] | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const zipM = useMutation({
    mutationFn: (connectionId: string) => downloadMdcpPhotosZip(connectionId),
  });

  const deleteM = useMutation({
    mutationFn: (photoId: string) => deleteMdcpPointPhoto(photoId),
    onSuccess: inval,
  });
  const rejectM = useMutation({
    mutationFn: (v: { reason: string; photoIds?: string[] }) =>
      rejectMdcpPhotos(orderId, v.reason, v.photoIds),
    onSuccess: () => {
      setRejectTarget(null);
      setRejectError(null);
      inval();
    },
    onError: (e) => setRejectError(apiErr(e)),
  });
  const restoreM = useMutation({
    // Pusta lista = „wszystkie odrzucone" — backend liczy ten zbiór sam, bez wysyłania id.
    mutationFn: (photoIds: string[]) =>
      restoreMdcpPhotos(orderId, photoIds.length > 0 ? photoIds : undefined),
    onSuccess: inval,
    onError: (e) => setActionError(apiErr(e)),
  });

  const points = data?.points ?? [];
  const hasPoints = points.length > 0;

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contextGroupRef = useRef<L.LayerGroup | null>(null);
  const pointsGroupRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);
  // Latest selection reachable from Leaflet click handlers without re-binding markers.
  const selectRef = useRef<(id: string) => void>(() => {});
  selectRef.current = (id) => {
    setSelectedId((cur) => (cur === id ? null : id));
    setActionError(null);
  };

  // Init the map once the container is mounted (the container only renders with data).
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { center: [52, 19], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    contextGroupRef.current = L.layerGroup().addTo(map);
    pointsGroupRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [hasPoints]);

  // Route + parcels underlay, redrawn only when the payload changes.
  useEffect(() => {
    const map = mapRef.current;
    const group = contextGroupRef.current;
    if (!map || !group || !data) return;
    group.clearLayers();
    const bounds = drawMdcpContext(group, data);
    for (const p of data.points) {
      if (!p.geometry) continue;
      const [lng, lat] = p.geometry.coordinates as [number, number];
      if (bounds) bounds.extend([lat, lng]);
    }
    if (!fittedRef.current) {
      map.invalidateSize();
      if (bounds?.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
        fittedRef.current = true;
      } else if (data.points.some((p) => p.geometry)) {
        const pts = data.points
          .filter((p) => p.geometry)
          .map((p) => {
            const [lng, lat] = p.geometry!.coordinates as [number, number];
            return [lat, lng] as [number, number];
          });
        map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 18 });
        fittedRef.current = true;
      }
    }
  }, [data]);

  // Point markers — cheap to redraw on every selection change (dashed ring).
  useEffect(() => {
    const group = pointsGroupRef.current;
    if (!group) return;
    group.clearLayers();
    for (const p of points) {
      if (!p.geometry) continue;
      const [lng, lat] = p.geometry.coordinates as [number, number];
      const m = L.marker([lat, lng], { icon: mdcpPointIcon(p, p.id === selectedId), zIndexOffset: 1000 });
      m.on("click", () => selectRef.current(p.id));
      m.bindTooltip(
        `Punkt #${p.number} · ${p.azimuth_deg}°${p.note ? ` · ${p.note}` : ""}` +
          (p.photos_count > 0 ? ` · ${p.photos_count} zdj.` : ""),
      );
      m.addTo(group);
    }
  }, [points, selectedId]);

  // Non-MDCP order (404) or no points → the whole section self-hides.
  if (!data || points.length === 0) return null;

  const selected = points.find((p) => p.id === selectedId) ?? null;
  const withPhoto = points.filter((p) => p.photos_count > 0).length;
  // Odbiór zdjęć: tylko zamawiający i tylko w zamówieniu niezakończonym — backend liczy tę
  // samą bramkę, tu decyduje wyłącznie o tym, czy przyciski w ogóle się pokazują.
  const canReview = data.can_review_photos;
  const acceptedCount = points.reduce((n, p) => n + p.photos_count, 0);
  const rejectedCount = points.reduce((n, p) => n + p.rejected_photos_count, 0);

  // Flat photo list (all points) — powers the lightbox + shared naming with the ZIP.
  const allPhotos = buildMdcpLightbox(points);
  const photoIndexById = new Map(allPhotos.map((ph, i) => [ph.id, i]));
  const connectionId = points[0]?.connection ?? null;

  const selectFromList = (p: (typeof points)[number]) => {
    selectRef.current(p.id);
    if (p.geometry && mapRef.current) {
      const [lng, lat] = p.geometry.coordinates as [number, number];
      mapRef.current.panTo([lat, lng]);
    }
  };

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold border-l-2 border-[var(--color-primary)] pl-2">
        <Camera size={15} /> Punkty zdjęciowe MDCP
        <span className="text-xs font-normal text-[var(--color-text-muted)]">
          {withPhoto}/{points.length} ze zdjęciem
          {rejectedCount > 0 ? ` · ${rejectedCount} odrzuconych` : ""}
        </span>
        <span className="ml-auto" />
        {canReview && acceptedCount > 0 && (
          <button
            type="button"
            disabled={rejectM.isPending}
            onClick={() => {
              setRejectError(null);
              setRejectTarget([]);
            }}
            title="Odrzuć cały materiał zdjęciowy tego zamówienia (z powodem)"
            className="inline-flex items-center gap-1.5 border border-[var(--color-primary)] px-2 py-0.5 text-[11px] font-normal text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] disabled:opacity-60"
          >
            <Ban size={12} /> Odrzuć wszystkie
          </button>
        )}
        {canReview && rejectedCount > 0 && (
          <button
            type="button"
            disabled={restoreM.isPending}
            onClick={() => restoreM.mutate([])}
            title="Cofnij wszystkie odrzucenia w tym zamówieniu"
            className="inline-flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-normal hover:bg-[var(--color-bg-elevated)] disabled:opacity-60"
          >
            {restoreM.isPending ? <Spinner /> : <RotateCcw size={12} />} Przywróć odrzucone
          </button>
        )}
        {withPhoto > 0 && connectionId && (
          <button
            type="button"
            disabled={zipM.isPending}
            onClick={() => zipM.mutate(connectionId)}
            title="Pobierz wszystkie zdjęcia jako ZIP (PUNKT_<nr>__ZDJ_<i>)"
            className="inline-flex items-center gap-1.5 border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-normal hover:bg-[var(--color-bg-elevated)] disabled:opacity-60"
          >
            {zipM.isPending ? <Spinner /> : <FileArchive size={12} />} ZIP
          </button>
        )}
      </h2>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="lg:col-span-2 relative isolate z-0 space-y-1">
          <div
            ref={containerRef}
            className="w-full border border-[var(--color-border)]"
            style={{ background: "#fff", height: "380px" }}
          />
          {/* Ten sam rodzaj linii (kablowa/napowietrzna), który widzi geodeta w zamówieniu. */}
          <LinePlacementLegend summary={data.route?.line_placement} />
        </div>

        {/* Klik w punkt (mapa lub lista) → jego zdjęcia tutaj, po prawej. */}
        <div className="border border-[var(--color-border)] p-3 space-y-3 min-h-[120px]">
          {selected ? (
            <>
              <MdcpSelectedPointHeader point={selected} />
              <MdcpPointPhotoGrid
                photos={selected.photos}
                onOpen={(ph) => setLightboxIndex(photoIndexById.get(ph.id) ?? 0)}
                overlay={(ph) => (
                  <>
                    {canReview &&
                      (ph.is_rejected ? (
                        <button
                          type="button"
                          title="Cofnij odrzucenie (zdjęcie wraca do materiału zamówienia)"
                          disabled={restoreM.isPending}
                          onClick={() => restoreM.mutate([ph.id])}
                          className="bg-white/90 p-0.5 text-[var(--color-text-muted)] hover:text-green-600 disabled:opacity-60"
                        >
                          <RotateCcw size={13} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Odrzuć zdjęcie (z powodem)"
                          onClick={() => {
                            setRejectError(null);
                            setRejectTarget([ph.id]);
                          }}
                          className="bg-white/90 p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Ban size={13} />
                        </button>
                      ))}
                    <button
                      type="button"
                      title="Usuń zdjęcie"
                      disabled={deleteM.isPending}
                      onClick={() => deleteM.mutate(ph.id)}
                      className="bg-white/90 p-0.5 text-[var(--color-text-muted)] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              />
              {actionError && (
                <p className="text-[11px] font-mono text-[var(--color-primary)]">{actionError}</p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Kliknij punkt na mapie (lub na liście poniżej), aby zobaczyć jego zdjęcia.
            </p>
          )}
        </div>
      </div>

      <MdcpPointsList
        points={points}
        selectedId={selectedId}
        onSelect={selectFromList}
      />

      <MdcpPointsFootnote points={points} />

      <MdcpPhotoRejectModal
        open={rejectTarget !== null}
        scope={rejectTarget !== null && rejectTarget.length === 0 ? "all" : "one"}
        count={rejectTarget !== null && rejectTarget.length === 0 ? acceptedCount : rejectTarget?.length ?? 0}
        pending={rejectM.isPending}
        error={rejectError}
        onClose={() => {
          setRejectTarget(null);
          setRejectError(null);
        }}
        onConfirm={(reason) =>
          rejectM.mutate({
            reason,
            // Pusta lista = „wszystkie": backend sam bierze cały nieodrzucony materiał zamówienia,
            // więc nie wysyłamy setki identyfikatorów, które i tak mógłby wyliczyć sam.
            photoIds: rejectTarget && rejectTarget.length > 0 ? rejectTarget : undefined,
          })
        }
      />

      {lightboxIndex !== null && allPhotos.length > 0 && (
        <MdcpPhotoLightbox
          photos={allPhotos}
          index={Math.min(lightboxIndex, allPhotos.length - 1)}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDownloadZip={connectionId ? () => zipM.mutate(connectionId) : undefined}
          zipPending={zipM.isPending}
        />
      )}
    </section>
  );
}
